import { motion } from "framer-motion";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import usePageSEO from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import { lvpFAQs } from "@/data/faqData";
import RelatedServices from "@/components/RelatedServices";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import { Link } from "react-router-dom";
import { Shield, Layers, Ruler, Gem } from "lucide-react";
import heroImg from "@/assets/brand-cali-floors.webp";
import FullServiceCallout from "@/components/FullServiceCallout";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const LVPInstallation = () => {
  usePageSEO({
    title: "Michigan LVP Installation and Forensic Subfloor Engineering",
    description: "Luxury vinyl plank installation with NWFA-grade subfloor engineering. WPC vs SPC core science, flush-mount transitions, 1/8-inch flatness mandate.",
    path: "/lvp-installation",
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ══ HERO ══ */}
        <section className="relative min-h-[560px] max-h-[800px] h-[75svh] flex items-end overflow-hidden bg-foreground">
          <PageBreadcrumb variant="overlay" items={[
            { label: "Home", to: "/" },
            { label: "Services" },
            { label: "LVP Installation" },
          ]} />
          <div className="absolute inset-0">
            <img width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async"
              src={heroImg}
              alt="Luxury vinyl plank installation over leveled subfloor in Metro Detroit MI 1 Wood Floors"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/50 to-foreground/20" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
          >
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Luxury Vinyl Plank Installation Michigan
            </p>
            <h1 className="font-display text-[clamp(28px,5.5vw,58px)] font-semibold leading-[1.08] tracking-tight text-primary-foreground">
              Michigan LVP Installation and Forensic Subfloor Engineering
            </h1>
            <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              SPC or WPC, rigid core or flexible. The technology only performs when the subfloor is engineered to perfection. We bring 25 years of hardwood precision to every vinyl plank we lay.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <QuoteModal
                trigger={
                  <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                    Request a Forensic Site Evaluation →
                  </button>
                }
              />
              <a
                href="tel:2483258853"
                className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center"
              >
                Call (248) 325-8853
              </a>
            </div>
          </motion.div>
        </section>

        {/* ══ THE PHYSICS OF THE CORE ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Core Technology</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The Physics of the Core
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }} className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">SPC Stone Plastic Composite</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      SPC cores are harder, denser, and more durable than any other vinyl construction. But because they are rigid, they are completely unforgiving to subfloor imperfections. A dip of more than 1/8 inch over 10 feet will cause the tongue-and-groove locking system to snap under foot traffic. The plank cannot flex to absorb the void. It fractures instead.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }} className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">WPC Wood Plastic Composite</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      WPC cores offer more cushion underfoot and better sound absorption than SPC. The softer composite provides slight flex, but this is not a license to skip subfloor prep. WPC still demands tight flatness tolerances to prevent joint separation and telegraphing over time. Every vinyl floor, regardless of core type, lives or dies on the subfloor beneath it.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ THE 1/8-INCH MANDATE ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Forensic Subfloor Prep</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                The 1/8 Inch Mandate
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Every luxury vinyl plank manufacturer specifies the same tolerance: no more than 3/16 inch variation over 10 feet. We hold ourselves to 1/8 inch. Because on a rigid SPC floor, that extra 1/16 inch is the difference between a 20-year floor and a warranty claim in year two.
                </p>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Our forensic subfloor process begins with laser-guided flatness mapping. We identify every dip, crown, and transition point before a single plank is unpacked. Then we engineer the correction using{" "}
                  <Link to="/subfloor-leveling" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    engineered subfloor preparation
                  </Link>
                  {" "}including self-leveling underlayment, mastic encapsulation, and mechanical grinding. The result is a substrate so flat that the locking system never bears structural load. It only connects.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <Ruler className="w-5 h-5 text-primary flex-shrink-0" />
                  <p className="text-xs font-semibold tracking-wide uppercase text-primary">
                    Non-negotiable flatness tolerance: 1/8 inch over 10 feet
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ THE 20-MIL STANDARD ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Wear Layer Science</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The 20 Mil Standard
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The image you see on a vinyl plank is a high-definition photograph printed beneath a clear urethane wear layer. Cheap LVP uses 6 to 12 mil wear layers that scratch through within a few years, exposing the printed image to damage. We specify 20-mil wear layers as our baseline.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  But thickness alone is not enough. We look for high-definition textures where the surface embossing aligns with the printed grain image. This technique, called Registered Embossing or EIR (Embossed In Register), creates a tactile experience that mimics the feel of real hardwood. When guests run their hand across the plank, they feel grain. Not plastic.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ ARCHITECTURAL JEWELRY ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Premium Finishing Details</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                Architectural Jewelry
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-8"
              >
                <Gem className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-display text-lg font-semibold text-primary-foreground mb-3">Ali Trims by Stairrods</h3>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  We eliminate cheap plastic T-moldings from every project. In their place, we install precision-machined Ali Trims from Stairrods UK. These aluminum and brass transition profiles create clean, permanent joints between flooring types. Available in brushed chrome, antique brass, satin nickel, and matte black, they transform a functional transition into an architectural statement.
                </p>
              </motion.div>

              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-primary-foreground/[.04] border border-primary-foreground/10 rounded-2xl p-6 md:p-8"
              >
                <Layers className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-display text-lg font-semibold text-primary-foreground mb-3">TOP Step Flush Mount Vents</h3>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Standard raised metal registers create trip hazards and break the visual plane of a floor. Our TOP Step flush-mount vents sit perfectly level with the flooring surface, matched to the exact grain pattern and color of your LVP. The result is a seamless, furniture-grade finish that most visitors never notice. That is the point.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ LICENSED BUILDER CALLOUT ══ */}
        <FullServiceCallout variant="lvp" />

        {/* ══ CTA ══ */}
        <section className="bg-primary py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
                Your floor deserves forensic-level <em className="italic font-normal text-[hsl(var(--gold-300))]">precision.</em>
              </h3>
              <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
                We engineer the subfloor, specify the core, and finish with architectural details that elevate LVP beyond its category.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
              <QuoteModal
                trigger={
                  <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                    Schedule a Forensic Site Evaluation →
                  </button>
                }
              />
              <a
                href="tel:2483258853"
                className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center"
              >
                📞 (248) 325-8853
              </a>
            </div>
          </div>
        </section>

        <RelatedServices
          heading="Every great LVP floor starts beneath the surface."
          subheading="Foundation & Product Selection"
          links={[
            { title: "Subfloor Leveling & Repair", desc: "The foundation beneath every successful LVP installation. Laser-guided flatness mapping, self-leveling compounds, and mechanical grinding to hit our 1/8-inch mandate.", to: "/subfloor-leveling", tag: "Foundation" },
            { title: "CALI Luxury Vinyl Collections", desc: "Browse all eleven CALI Vinyl collections featuring 20-mil wear layers, 100% waterproof SPC and WPC cores, and 50-year residential warranties.", to: "/brands/cali", tag: "Product Selection" },
          ]}
        />

        <GoogleReviewsCarousel contextTags={["installation"]} count={4} />
        <PageFAQ suppressJsonLd faqs={lvpFAQs} heading={<>LVP installation questions <em className="italic font-normal text-primary">answered.</em></>} subheading="LVP Installation FAQs" />
      </main>
      <SiteFooter />
    </div>
  );
};

export default LVPInstallation;
